import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Button, LoadingSpinner } from '@atrvd/saas-ui'
import { useBankConnection, useFacilityConnectionAccountCounts } from '@/hooks/useBankConnections'
import { useLedgerSyncCompletionWatcher } from '@/hooks/useLedgerSyncCompletionWatcher'
import { useFacilityScope } from '@/hooks/useFacilityScope'
import { useSetBreadcrumb } from '@/components/layout/BreadcrumbContext'
import { ConnectionDetailHeader } from '@/components/ledgersync/ConnectionDetailHeader'
import { ConnectionAccountsTable } from '@/components/ledgersync/ConnectionAccountsTable'
import { AddAccountsToFacilityDialog } from '@/components/ledgersync/AddAccountsToFacilityDialog'
import { CONNECTION_COPY } from '@/components/ledgersync/connectionManagement.utils'
import { FinancialAccountsPageHeader } from '@/components/financial-accounts/FinancialAccountsPageHeader'
import { FacilityScopeFallback } from '@/components/financial-accounts/FacilityScopeFallback'
import { financialAccountsBasePath } from '@/components/financial-accounts/financialAccountsScope'
import { useFacilityScopeBreadcrumb } from '@/components/financial-accounts/useFacilityScopeBreadcrumb'
import { ReadOnlyTooltip } from '@/components/financial-accounts/ReadOnlyControls'

/**
 * One bank connection and its accounts (DCP-324, Screen 2).
 *
 * Routes: `/settings/financial-accounts/connections/:connectionId`, and the
 * same screen under a facility,
 * `/settings/facilities/:facilityId/financial-accounts/connections/:connectionId`,
 * where it shows only that facility's accounts and counts.
 *
 * A ROUTE, not a modal: the accounts table is long, lazily loaded and
 * page-scrolled, and `Modal`'s body already scrolls — putting it in a dialog
 * would nest a second scroll container.
 */
export function ConnectionAccountsPage() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const navigate = useNavigate()
  const facility = useFacilityScope()
  const { scope } = facility
  const basePath = financialAccountsBasePath(scope)
  const [addOpen, setAddOpen] = useState(false)

  const { data: connection, connections, dataUpdatedAt, isLoading, error } =
    useBankConnection(connectionId)
  const facilityCounts = useFacilityConnectionAccountCounts(scope?.facilityId ?? null)

  // DCP-401: this route never shares the screen with the list section, so it
  // mounts its own watcher — a Sync pressed here must refresh the accounts
  // table below and report its result when the run finishes. The whole list is
  // watched, not just this row, so a "Sync all" started on the list and still
  // running when the user drilled in is reported here too.
  useLedgerSyncCompletionWatcher(connections, dataUpdatedAt)

  // The connection's NAME for its `:connectionId` crumb, rather than a raw uuid;
  // and, on a facility route, "{Facility} Financial Accounts" for the crumb above.
  useSetBreadcrumb(connectionId, connection?.display_name ?? undefined)
  useFacilityScopeBreadcrumb(scope)

  if (!connectionId) {
    return (
      <div className="py-12 text-center">
        <p className="text-destructive">Invalid connection ID</p>
      </div>
    )
  }

  // A facility route whose facility has not resolved: spinner, error or "no
  // longer exists" — never the org-wide page in the meantime.
  if (facility.scoped && !scope) {
    return <FacilityScopeFallback isLoading={facility.isLoading} error={facility.error} notFound={facility.notFound} />
  }

  const loading = isLoading || (!!scope && facilityCounts.isLoading)
  // Only a load that never produced data replaces the page: a failed background
  // refetch keeps the cached connection and counts on screen.
  const loadError = (connections ? null : error) ?? (scope && !facilityCounts.data ? facilityCounts.error : null)

  return (
    <div className="space-y-6">
      {/* The page keeps the settings page's heading (the facility's name when
          scoped); the connection's own name is the heading of the card below. */}
      <FinancialAccountsPageHeader scope={scope} backToList />

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : loadError ? (
        <div className="py-12 text-center">
          <p className="text-destructive">
            {loadError instanceof Error ? loadError.message : 'Failed to load the connection'}
          </p>
        </div>
      ) : !connection ? (
        // Not an error: the row is genuinely gone (deleted in another tab, or a
        // stale bookmark). The list is the only useful next step.
        <div className="py-12 text-center">
          <p className="text-muted-foreground">This connection no longer exists.</p>
        </div>
      ) : (
        <>
          <ConnectionDetailHeader
            // The org-wide row drives the status rules; only the meta line's
            // "N accounts & cards" is the facility's own count when scoped.
            connection={connection}
            accountsCount={scope ? (facilityCounts.data?.[connection.id] ?? 0) : connection.accounts_count}
            scope={scope}
            // Deleting the connection from its own page leaves nothing to show,
            // so navigate back to the list it was opened from.
            onDeleted={() => navigate(basePath, { replace: true })}
          />
          <ConnectionAccountsTable
            connectionId={connection.id}
            scope={scope}
            headingAction={
              scope ? (
                <ReadOnlyTooltip readOnly={scope.readOnly}>
                  <Button
                    size="sm"
                    className="gap-2"
                    disabled={scope.readOnly}
                    onClick={() => setAddOpen(true)}
                  >
                    <Plus aria-hidden="true" className="h-4 w-4" />
                    {CONNECTION_COPY.addToFacilityCta}
                  </Button>
                </ReadOnlyTooltip>
              ) : undefined
            }
          />
          {scope && !scope.readOnly && addOpen && (
            <AddAccountsToFacilityDialog
              connectionId={connection.id}
              institutionName={connection.display_name ?? CONNECTION_COPY.unknownInstitution}
              scope={scope}
              onClose={() => setAddOpen(false)}
            />
          )}
        </>
      )}
    </div>
  )
}
